import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { cn } from '@epoch-ui/lib/utils';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      ref={ref}
      className={cn(
        'tw-flex tw-flex-col !tw-gap-2', // Vertical alignment with gaps
        className
      )}
      {...props}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & { description?: string }
>(({ className, value, description, ...props }, ref) => {
  const itemId = React.useId();
  const labelId = `${itemId}-label`;
  const descriptionId = `${itemId}-description`;

  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      value={value}
      aria-labelledby={labelId}
      aria-describedby={description ? descriptionId : undefined}
      className={cn(
        'tw-flex tw-cursor-pointer tw-gap-3 tw-rounded-md tw-pl-0 ',
        description ? 'tw-items-start' : 'tw-items-center',
        'tw-text-primary focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary focus-visible:tw-ring-offset-2',
        'disabled:tw-cursor-not-allowed disabled:tw-opacity-50',
        className
      )}
      {...props}
    >
      <div className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full">
        <RadioGroupPrimitive.Indicator
          forceMount
          className={cn(
            'tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-full',
            'tw-border-solid tw-border-border-grey data-[state=checked]:tw-border-0 data-[state=unchecked]:tw-border-[1.5px]',
            'data-[state=checked]:tw-bg-primary-blue data-[state=unchecked]:tw-bg-white'
          )}
        >
          <div className="tw-h-[8px] tw-w-[8px] tw-rounded-lg tw-bg-white" />
        </RadioGroupPrimitive.Indicator>
      </div>
      {description ? (
        <span className="tw-flex tw-flex-col tw-gap-1 tw-text-left">
          <span id={labelId} className="tw-text-sm tw-font-normal tw-text-dark-black">
            {props.children}
          </span>
          <span id={descriptionId} className="tw-text-xs tw-leading-4 tw-text-gray-500">
            {description}
          </span>
        </span>
      ) : (
        <span id={labelId} className="tw-text-sm tw-font-normal tw-text-dark-black">
          {props.children}
        </span>
      )}
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
